import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Brain,
  CheckCircle2,
  Clock,
  Sparkles,
  XCircle,
  MessageSquareQuote,
} from "lucide-react";
import { log } from "../lib/log";

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString("en-US") : n ?? "—";

const fmtTime = (iso, isAr) => {
  if (!iso) return isAr ? "—" : "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return isAr ? `قبل ${Math.round(diff)} ث` : `${Math.round(diff)}s ago`;
  if (diff < 3600) return isAr ? `قبل ${Math.round(diff / 60)} د` : `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return isAr ? `قبل ${Math.round(diff / 3600)} س` : `${Math.round(diff / 3600)}h ago`;
  return isAr ? `قبل ${Math.round(diff / 86400)} ي` : `${Math.round(diff / 86400)}d ago`;
};

export default function SamiSelfLearnPanel() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sami/self-learn-stats");
      setData(data);
      setErr(false);
    } catch {
      setErr(true);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const reject = async (id) => {
    setRejecting(id);
    try {
      await api.post(`/admin/sami/self-learn/reject/${id}`);
      await load();
    } catch (err) {
      // Admin-only action — surface the failure rather than silently
      // swallowing it.
      // eslint-disable-next-line no-console
      log.error("self-learn reject failed:", err);
      toast.error(
        err?.response?.data?.detail || "Could not reject Q&A",
      );
    } finally {
      setRejecting(null);
    }
  };

  if (err) return null;
  if (!data) {
    return (
      <div
        data-testid="sami-selflearn-loading"
        className="border border-slate-200 bg-white p-5 text-slate-500 text-sm"
      >
        {isAr ? "جاري تحميل لوحة التعلّم الذاتي…" : "Loading self-learning panel…"}
      </div>
    );
  }

  const {
    captured_total,
    pending_self,
    learned_total,
    learned_last_hour,
    learned_last_24h,
    rejected_total,
    worker_running,
    recent_learned = [],
  } = data;

  return (
    <div
      data-testid="sami-selflearn-panel"
      className="border border-slate-200 bg-white p-6 lg:p-8 mb-8"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-slate-500" />
            <p className="eyebrow">
              {isAr ? "التعلّم الذاتي لسامي" : "Sami self-learning"}
            </p>
          </div>
          <h3
            data-testid="sami-selflearn-headline"
            className="font-display text-3xl lg:text-4xl mt-2 tracking-tight"
          >
            {fmt(learned_total)}{" "}
            <span className="text-slate-400 text-xl">
              {isAr ? "سؤال تعلّمه تلقائياً" : "auto-learned"}
            </span>
          </h3>
        </div>

        <div className="flex items-center gap-2 mt-2">
          {worker_running ? (
            <span
              data-testid="selflearn-worker-running"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium"
            >
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              {isAr ? "العامل نشط" : "Worker active"}
            </span>
          ) : (
            <span
              data-testid="selflearn-worker-stopped"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium"
            >
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              {isAr ? "العامل متوقّف" : "Worker stopped"}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-6">
        <StatTile
          icon={<MessageSquareQuote className="w-3 h-3" />}
          label={isAr ? "أُضيف من المحادثات" : "Captured"}
          value={fmt(captured_total)}
          testid="selflearn-captured-total"
        />
        <StatTile
          icon={<Clock className="w-3 h-3" />}
          label={isAr ? "قائمة الانتظار" : "Pending"}
          value={fmt(pending_self)}
          testid="selflearn-pending"
          tone={pending_self > 0 ? "active" : "muted"}
        />
        <StatTile
          icon={<CheckCircle2 className="w-3 h-3" />}
          label={isAr ? "في آخر ساعة" : "Last hour"}
          value={fmt(learned_last_hour)}
          testid="selflearn-last-hour"
          tone={learned_last_hour > 0 ? "active" : "muted"}
        />
        <StatTile
          icon={<Sparkles className="w-3 h-3" />}
          label={isAr ? "آخر 24 ساعة" : "Last 24h"}
          value={fmt(learned_last_24h)}
          testid="selflearn-last-24h"
        />
        <StatTile
          icon={<XCircle className="w-3 h-3" />}
          label={isAr ? "مرفوض جودة" : "Rejected"}
          value={fmt(rejected_total)}
          testid="selflearn-rejected"
          tone="muted"
        />
      </div>

      {recent_learned.length > 0 ? (
        <div data-testid="selflearn-recent">
          <p className="eyebrow mb-3">
            {isAr ? "أحدث ما تعلّمه" : "Most-recently learned"}
          </p>
          <ul className="space-y-2">
            {recent_learned.map((qa) => {
              const open = expanded === qa.id;
              return (
                <li
                  key={qa.id}
                  data-testid={`selflearn-item-${qa.id}`}
                  className="border border-slate-200 bg-slate-50/50"
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : qa.id)}
                    dir="auto"
                    className="w-full text-start px-4 py-3 flex items-center gap-3 hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 shrink-0">
                      {fmtTime(qa.answered_at, isAr)}
                    </span>
                    <span className="text-sm text-slate-800 flex-1 line-clamp-1">
                      {qa.question}
                    </span>
                  </button>
                  {open && (
                    <div className="px-4 pb-4 pt-1 border-t border-slate-200">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-3 mb-2">
                        {isAr ? "إجابة سامي" : "Sami's answer"}
                      </p>
                      <div
                        dir="auto"
                        className="text-[13px] leading-[1.85] text-slate-700 whitespace-pre-wrap max-h-72 overflow-y-auto bg-white border border-slate-200 p-3"
                      >
                        {qa.answer}
                      </div>
                      <button
                        type="button"
                        disabled={rejecting === qa.id}
                        onClick={() => reject(qa.id)}
                        data-testid={`selflearn-reject-${qa.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border border-rose-200 text-rose-700 hover:bg-rose-50 transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        {rejecting === qa.id
                          ? isAr ? "جاري الرفض…" : "Rejecting…"
                          : isAr ? "رفض الجودة" : "Reject quality"}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="selflearn-empty">
          {isAr
            ? "لم يتعلّم سامي شيئاً بعد. سيبدأ بعد أول سؤال قانوني حقيقي من زائر."
            : "Sami hasn't auto-learned anything yet. The loop kicks in on the first real legal question from a visitor."}
        </p>
      )}
    </div>
  );
}

function StatTile({ icon, label, value, testid, tone = "default" }) {
  const accent =
    tone === "active"
      ? "bg-emerald-50 border-emerald-200"
      : tone === "muted"
        ? "bg-slate-50 border-slate-100"
        : "bg-white border-slate-200";
  return (
    <div
      data-testid={testid}
      className={`border ${accent} px-3 py-2.5`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-display text-lg lg:text-xl mt-1 tracking-tight text-slate-900">
        {value}
      </div>
    </div>
  );
}
